import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type FormEvent } from 'react';
import { api, ApiError } from '../api';
import { can, fieldVisible } from '../auth/me';
import { useMe } from '../auth/useMe';
import { parsePln, plnToInput } from '../catalog/money';
import { todayInWarsaw } from '../events/calendar';
import { formatDayText } from '../events/fields';
import { ConflictNotice } from '../ui/ConflictNotice';
import { QuickPicker, type PickerOption } from '../ui/QuickPicker';
import { BARTER_KEY, BARTER_RESOURCE, DIRECTION_LABELS, type Direction, type EntryView } from './types';
import { sameValues, useOpening, useUnsavedGuard } from '../ui/unsaved';

type Ref = { id: string; name: string };
type Option = PickerOption & { id: string };

async function searchContractors(q: string, signal: AbortSignal): Promise<Option[]> {
  const res = await api<{ contractors: Ref[] }>(`/api/contractors?q=${encodeURIComponent(q)}`, { signal });
  return res.contractors.slice(0, 20).map((c) => ({ key: c.id, id: c.id, name: c.name, details: [] }));
}
async function searchEvents(q: string, signal: AbortSignal): Promise<Option[]> {
  const res = await api<{ events: { id: string; name?: string; startsAt?: string }[] }>(`/api/events?q=${encodeURIComponent(q)}`, { signal });
  return res.events.slice(0, 20).map((e) => ({ key: e.id, id: e.id, name: e.name ?? 'Wydarzenie', details: e.startsAt ? [formatDayText(todayInWarsaw(new Date(e.startsAt)))] : [] }));
}

/**
 * Adding or changing a barter entry (F43): contractor, "daliśmy / dostaliśmy", description, value, day and optionally
 * an event. On edit only what changed is sent; fields the role does not see are neither shown nor sent (rule 7a).
 */
export function BarterEntryForm({ entry, contractor, onClose }: { entry?: EntryView; contractor?: Ref | null; onClose: () => void }) {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const [who, setWho] = useState<Ref | null>(entry ? { id: entry.contractorId, name: entry.contractorName } : (contractor ?? null));
  const [event, setEvent] = useState<Ref | null>(entry?.eventId ? { id: entry.eventId, name: entry.eventName ?? '' } : null);
  const [direction, setDirection] = useState<Direction>(entry?.direction ?? 'dalismy');
  const [description, setDescription] = useState(entry?.description ?? '');
  const [amount, setAmount] = useState(entry?.valueGrosze !== undefined ? plnToInput(entry.valueGrosze) : '');
  const [day, setDay] = useState(entry?.occurredOn ?? todayInWarsaw());
  const [invalid, setInvalid] = useState('');
  const save = useMutation({
    mutationFn: (fields: Record<string, unknown>) =>
      entry ? api(`/api/barter/entries/${entry.id}`, { method: 'PATCH', body: { version: entry.version, ...fields } }) : api('/api/barter/entries', { method: 'POST', body: fields }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: BARTER_KEY });
      onClose();
    },
  });
  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const now = { who: who?.id ?? null, event: event?.id ?? null, direction, description, amount, day };
  const opening = useOpening(now);
  const saveNow = useRef<() => Promise<boolean>>(async () => false);
  const guardRef = useUnsavedGuard({ dirty: !sameValues(now, opening), save: () => saveNow.current() });
  if (!me) return null;
  const sees = (f: string) => fieldVisible(me, BARTER_RESOURCE, f);
  const seeEvents = can(me, 'events.events', 'view');

  /** What to send (only what changed), or null with the reason shown. */
  function toSend(): Record<string, unknown> | null {
    const fail = (why: string) => (setInvalid(why), null);
    setInvalid('');
    if (!who) return fail('Wybierz kontrahenta');
    if (description.trim() === '') return fail('Opisz, co daliśmy lub dostaliśmy');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return fail('Podaj datę');
    let valueGrosze: number | undefined;
    if (sees('valueGrosze')) {
      try {
        valueGrosze = parsePln(amount);
      } catch (error) {
        return fail(error instanceof Error ? error.message : 'Nieprawidłowa wartość');
      }
      if (valueGrosze <= 0) return fail('Wartość musi być większa od zera');
    }
    const eventId = event?.id ?? null;
    const fields: Record<string, unknown> = {
      ...(!entry || who.id !== entry.contractorId ? { contractorId: who.id } : {}),
      ...(seeEvents && (entry ? eventId !== entry.eventId : eventId !== null) ? { eventId } : {}),
      ...(!entry || direction !== entry.direction ? { direction } : {}),
      ...(!entry || description.trim() !== entry.description ? { description: description.trim() } : {}),
      ...(valueGrosze !== undefined && (!entry || valueGrosze !== entry.valueGrosze) ? { valueGrosze } : {}),
      ...(!entry || day !== entry.occurredOn ? { occurredOn: day } : {}),
    };
    return fields;
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    const fields = toSend();
    if (!fields) return;
    if (entry && Object.keys(fields).length === 0) return onClose();
    save.mutate(fields);
  }
  saveNow.current = async () => {
    const fields = toSend();
    if (!fields) return false;
    if (!entry || Object.keys(fields).length > 0) await save.mutateAsync(fields);
    return true;
  };
  const error = save.error instanceof ApiError ? save.error : null;
  return (
    <form ref={guardRef} className="group barter-form" onSubmit={submit} noValidate aria-label={entry ? 'Zmiana wpisu barteru' : 'Nowy wpis barteru'}>
      <div className="segment" role="radiogroup" aria-label="Kierunek">
        {(Object.keys(DIRECTION_LABELS) as Direction[]).map((d) => (
          <button key={d} type="button" role="radio" aria-checked={direction === d} aria-pressed={direction === d} onClick={() => setDirection(d)}>
            {DIRECTION_LABELS[d]}
          </button>
        ))}
      </div>
      <div className="field-row">
        {who ? (
          <p className="rental-item">
            <span className="muted">Kontrahent</span> <strong>{who.name}</strong>{' '}
            <button type="button" className="btn small ghost" onClick={() => setWho(null)}>
              Zmień
            </button>
          </p>
        ) : (
          <QuickPicker<Option> label="Kontrahent" placeholder="Szukaj kontrahenta…" search={searchContractors} onPick={(o) => setWho({ id: o.id, name: o.name })} />
        )}
      </div>
      <div className="field-row">
        <label className="field">
          <span>Opis</span>
          <input type="text" maxLength={2000} value={description} placeholder="np. nasze odsłuchy na ich wydarzenie" onChange={(e) => setDescription(e.target.value)} />
        </label>
        {sees('valueGrosze') && (
          <label className="field narrow-field">
            <span>Wartość (zł)</span>
            <input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} onBlur={() => { try { setAmount(plnToInput(parsePln(amount))); } catch { /* the message comes on saving */ } }} />
          </label>
        )}
        <label className="field narrow-field">
          <span>Data</span>
          <input type="date" value={day} onChange={(e) => setDay(e.target.value)} />
        </label>
      </div>
      {seeEvents && (
        <div className="field-row">
          {event ? (
            <p className="rental-item">
              <span className="muted">Wydarzenie</span> <strong>{event.name}</strong>{' '}
              <button type="button" className="btn small ghost" onClick={() => setEvent(null)}>
                Usuń
              </button>
            </p>
          ) : (
            <QuickPicker<Option> label="Wydarzenie (opcjonalnie)" placeholder="Szukaj wydarzenia…" search={searchEvents} onPick={(o) => setEvent({ id: o.id, name: o.name })} />
          )}
        </div>
      )}
      {invalid && (
        <p className="alert" role="alert">
          {invalid}
        </p>
      )}
      {error?.status === 409 && <ConflictNotice onReload={() => { void queryClient.invalidateQueries({ queryKey: BARTER_KEY }); onClose(); }} />}
      {error && error.status !== 409 && (
        <p className="alert" role="alert">
          {error.message}
        </p>
      )}
      <div className="actions">
        <button className="btn primary small" type="submit" disabled={save.isPending}>
          {entry ? 'Zapisz' : 'Dodaj'}
        </button>
        <button className="btn small" type="button" onClick={onClose}>
          Anuluj
        </button>
      </div>
    </form>
  );
}
